import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UploadCloud,
  FileText,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Trophy,
  User,
  Building,
} from "lucide-react";
import { UserProfile, StaffMember, Answer } from "../../types";
import {
  uploadPastProposalDocument,
  analyzePastProposalDocument,
  saveHarvestedDocumentAnswers,
  DocumentHarvestCandidate,
  PastProposalOutcome,
} from "../../services/harvestDocumentService";
import { subscribeToAnswers } from "../../services/answersService";
import { detectResidualWarnings } from "../../utils/sanitizeClient";
import { calculateTextSimilarity } from "../../utils/textSimilarity";
import { MASTER_TEMPLATE_2026_ANSWERS } from "../../data/masterTemplateAnswers";

export interface ImportProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile | null;
  staff: StaffMember[];
  onImportCompleted?: (createdIds: string[]) => void;
}

const DEFAULT_SERVICE_TAGS = [
  "Translation",
  "Localization",
  "Interpreting (VRI/OPI)",
  "Desktop Publishing (DTP)",
  "Transcription",
  "Quality Assurance",
  "Compliance & HIPAA",
  "Security",
  "General",
];

const ImportProposalContent: React.FC<Omit<ImportProposalModalProps, "isOpen">> = ({
  onClose,
  user,
  staff,
  onImportCompleted,
}) => {
  const [step, setStep] = useState<"upload" | "review" | "success">("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [proposalTitle, setProposalTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [outcome, setOutcome] = useState<PastProposalOutcome>("won");
  const [selectedOwner, setSelectedOwner] = useState(
    user?.displayName || staff[0]?.displayName || "Mark Bishop",
  );
  const [reviewCadenceDays, setReviewCadenceDays] = useState(180);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [candidates, setCandidates] = useState<DocumentHarvestCandidate[]>([]);
  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(null);
  const [existingAnswers, setExistingAnswers] = useState<Answer[]>([]);
  const [savedCount, setSavedCount] = useState(0);

  // Subscribe to existing answers for novelty/duplicate detection
  useEffect(() => {
    const unsub = subscribeToAnswers(
      (items) => {
        setExistingAnswers(items.filter((a) => a.status !== "retired"));
      },
      (err) => console.warn("Failed to subscribe to answers in import modal:", err),
    );
    return () => unsub();
  }, []);

  const [isDragging, setIsDragging] = useState(false);

  const processSelectedFile = (file: File) => {
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith(".pdf") && !lowerName.endsWith(".docx")) {
      setErrorMessage("Please select a valid .pdf or .docx document.");
      return;
    }

    setSelectedFile(file);
    setErrorMessage(null);

    // Auto-suggest proposal title from filename if empty
    if (!proposalTitle) {
      const cleanName = file.name
        .replace(/\.[^/.]+$/, "")
        .replace(/[_-]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      setProposalTitle(cleanName);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processSelectedFile(file);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isProcessing) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processSelectedFile(file);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setErrorMessage("Please select a proposal file to upload.");
      return;
    }
    if (!proposalTitle.trim()) {
      setErrorMessage("Please provide a proposal or bid title.");
      return;
    }
    if (!user) {
      setErrorMessage("You must be logged in to import proposal documents.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      setProcessingStatus("Uploading document to secure storage...");
      const uploadResult = await uploadPastProposalDocument(selectedFile, user);

      setProcessingStatus(
        "Analyzing proposal structure & extracting modular answers with Gemini...",
      );
      const extracted = await analyzePastProposalDocument({
        documentPaths: [uploadResult.storagePath],
        proposalTitle: proposalTitle.trim(),
        clientName: clientName.trim() || undefined,
        outcome,
      });

      if (extracted.length === 0) {
        throw new Error(
          "No substantive narrative sections could be extracted from this document. Verify the file contains text-based proposal narratives.",
        );
      }

      // Check similarity against existing library answers
      const augmented: DocumentHarvestCandidate[] = extracted.map((c) => {
        let bestScore = 0;
        let bestTitle = "";
        let bestId = "";

        for (const ans of existingAnswers) {
          const sim = calculateTextSimilarity(c.sanitizedBody, ans.body);
          if (sim > bestScore) {
            bestScore = sim;
            bestTitle = ans.title;
            bestId = ans.id;
          }
        }

        const isDuplicate = bestScore >= 0.8;
        return {
          ...c,
          similarToAnswerId: bestScore > 0.4 ? bestId : undefined,
          similarToTitle: bestScore > 0.4 ? bestTitle : undefined,
          similarityScore: bestScore > 0.4 ? bestScore : undefined,
          selected: !isDuplicate,
        };
      });

      setCandidates(augmented);
      if (augmented.length > 0) {
        setExpandedCandidateId(augmented[0]?.id || null);
      }
      setStep("review");
    } catch (err) {
      console.error("Analysis error:", err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to analyze and extract proposal answers.",
      );
    } finally {
      setIsProcessing(false);
      setProcessingStatus("");
    }
  };

  const handleLoadMasterTemplate = () => {
    setProposalTitle("MLC Master RFP Template 2026");
    setOutcome("won");
    setErrorMessage(null);

    const augmented: DocumentHarvestCandidate[] = MASTER_TEMPLATE_2026_ANSWERS.map((c) => {
      let bestScore = 0;
      let bestTitle = "";
      let bestId = "";

      for (const ans of existingAnswers) {
        const sim = calculateTextSimilarity(c.sanitizedBody, ans.body);
        if (sim > bestScore) {
          bestScore = sim;
          bestTitle = ans.title;
          bestId = ans.id;
        }
      }

      const isDuplicate = bestScore >= 0.8;
      return {
        ...c,
        similarToAnswerId: bestScore > 0.4 ? bestId : undefined,
        similarToTitle: bestScore > 0.4 ? bestTitle : undefined,
        similarityScore: bestScore > 0.4 ? bestScore : undefined,
        selected: !isDuplicate,
      };
    });

    setCandidates(augmented);
    if (augmented[0]) {
      setExpandedCandidateId(augmented[0].id);
    }
    setStep("review");
  };

  const toggleCandidateSelection = (id: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c)));
  };

  const toggleSelectAll = (select: boolean) => {
    setCandidates((prev) => prev.map((c) => ({ ...c, selected: select })));
  };

  const updateCandidateTitle = (id: string, newTitle: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, title: newTitle } : c)));
  };

  const updateCandidateBody = (id: string, newBody: string) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, sanitizedBody: newBody } : c)));
  };

  const toggleCandidateTag = (id: string, tag: string) => {
    setCandidates((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const exists = c.serviceTags.includes(tag);
        const updated = exists ? c.serviceTags.filter((t) => t !== tag) : [...c.serviceTags, tag];
        return { ...c, serviceTags: updated.length > 0 ? updated : ["General"] };
      }),
    );
  };

  const handleSaveSelected = async () => {
    const selected = candidates.filter((c) => c.selected);
    if (selected.length === 0) {
      setErrorMessage("Please select at least one answer candidate to import.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    const selectedStaffMember = staff.find((s) => s.displayName === selectedOwner);
    const ownerEmail = selectedStaffMember?.email || user?.email || "";

    try {
      const createdIds = await saveHarvestedDocumentAnswers(candidates, {
        proposalTitle: proposalTitle.trim(),
        clientName: clientName.trim() || undefined,
        outcome,
        ownerName: selectedOwner,
        ownerEmail,
        cadenceDays: reviewCadenceDays,
        documentPath: selectedFile?.name,
      });

      setSavedCount(createdIds.length);
      setStep("success");
      if (onImportCompleted) {
        onImportCompleted(createdIds);
      }
    } catch (err) {
      console.error("Save error:", err);
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to save answers to the library.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => !isProcessing && onClose()}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl border border-slate-200/80 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-[#2ba5b5] dark:bg-teal-950/50">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {step === "upload" && "Import Past Proposal into Answer Library"}
                  {step === "review" && "Review & Curate Extracted Answers"}
                  {step === "success" && "Import Complete"}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {step === "upload" &&
                    "Upload a past completed proposal (.pdf or .docx). Gemini will deconstruct it into modular, vetted answers."}
                  {step === "review" &&
                    `Found ${candidates.length} reusable narrative answers. Select, edit, and categorize before saving.`}
                  {step === "success" && "The approved answers have been saved to your library."}
                </p>
              </div>
            </div>
            {!isProcessing && (
              <button
                type="button"
                onClick={onClose}
                className="btn-animate rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {errorMessage && (
              <div className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
                <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* STEP 1: Upload & Configure */}
            {step === "upload" && (
              <div className="space-y-5">
                {/* Quick Seed Master Template Banner */}
                <div className="rounded-xl border border-teal-200/90 bg-gradient-to-r from-teal-50/90 via-emerald-50/60 to-cyan-50/40 p-4 dark:border-teal-800/60 dark:from-teal-950/40 dark:via-emerald-950/30 dark:to-slate-900 shadow-2xs">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-[#2ba5b5] text-white shadow-2xs">
                          <Sparkles className="h-3.5 w-3.5" />
                        </span>
                        <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                          Quick Seed: 2026 Master RFP Template
                        </h4>
                        <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-bold text-teal-800 dark:bg-teal-900/60 dark:text-teal-300">
                          48 Vetted Answers
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
                        Load 48 pre-sanitized, modular answers from your 2026 Master Template
                        covering TEP QA, memoQ, HIPAA, Interpreting, WBE credentials, and case
                        studies.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleLoadMasterTemplate}
                      className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-2 text-xs font-semibold text-white shadow-2xs hover:bg-[#238a97] shrink-0 cursor-pointer"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Load Master Template
                    </button>
                  </div>
                </div>

                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200 dark:border-slate-700" />
                  </div>
                  <span className="relative bg-white px-3 text-[11px] font-medium uppercase tracking-wider text-slate-400 dark:bg-slate-900">
                    Or upload another proposal document
                  </span>
                </div>

                {/* File Drop Area */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    Proposal Document (.pdf or .docx) *
                  </label>
                  <label
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer ${
                      isDragging
                        ? "border-[#2ba5b5] bg-teal-50/50 dark:bg-teal-950/40 ring-4 ring-[#2ba5b5]/20 scale-[1.01]"
                        : "border-slate-300 bg-slate-50/50 hover:border-[#2ba5b5] hover:bg-teal-50/20 dark:border-slate-700 dark:bg-slate-800/40 dark:hover:border-[#2ba5b5]"
                    }`}
                  >
                    <UploadCloud
                      className={`h-10 w-10 mb-2 transition-transform duration-200 ${
                        isDragging ? "text-[#2ba5b5] scale-110" : "text-[#2ba5b5]"
                      }`}
                    />
                    {selectedFile ? (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-slate-900 dark:text-white">
                          {selectedFile.name}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB • Ready to analyze
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                          {isDragging
                            ? "Drop your proposal document here"
                            : "Click to browse or drag and drop past proposal document"}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          Supported formats: native PDF or Microsoft Word (.docx) up to 50MB
                        </p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={isProcessing}
                    />
                  </label>
                </div>

                {/* Form Fields Grid */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Proposal / Bid Title *
                    </label>
                    <input
                      type="text"
                      value={proposalTitle}
                      onChange={(e) => setProposalTitle(e.target.value)}
                      placeholder="e.g. Illinois Tollway Language Access RFP"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                      disabled={isProcessing}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Target Client / Agency in Document
                    </label>
                    <div className="relative">
                      <Building className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="e.g. Illinois Tollway (used for auto-sanitization)"
                        className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-2 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        disabled={isProcessing}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Proposal Outcome & Win Credit
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setOutcome("won")}
                        className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${
                          outcome === "won"
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                        disabled={isProcessing}
                      >
                        <Trophy className="h-3.5 w-3.5 text-amber-500" />
                        <span>Won Bid (+1 Win)</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setOutcome("submitted")}
                        className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition-all ${
                          outcome === "submitted"
                            ? "border-teal-300 bg-teal-50 text-[#2ba5b5] dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                        disabled={isProcessing}
                      >
                        <FileText className="h-3.5 w-3.5" />
                        <span>Submitted</span>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Default Answer Owner
                    </label>
                    <div className="relative">
                      <User className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <select
                        value={selectedOwner}
                        onChange={(e) => setSelectedOwner(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-2 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                        disabled={isProcessing}
                      >
                        {staff.map((s) => (
                          <option key={s.id} value={s.displayName}>
                            {s.displayName} {s.title ? `(${s.title})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                      Review Cadence
                    </label>
                    <select
                      value={reviewCadenceDays}
                      onChange={(e) => setReviewCadenceDays(Number(e.target.value))}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                      disabled={isProcessing}
                    >
                      <option value={90}>Every 90 Days (Quarterly)</option>
                      <option value={180}>Every 180 Days (Semi-Annual)</option>
                      <option value={365}>Every 365 Days (Annual)</option>
                    </select>
                  </div>
                </div>

                {isProcessing && (
                  <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 dark:border-teal-900/40 dark:bg-teal-950/30">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-[#2ba5b5]" />
                      <div>
                        <p className="text-xs font-semibold text-slate-900 dark:text-white">
                          {processingStatus || "Processing document..."}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Extracting Quality Assurance, HIPAA/Security, Interpreting, and Tech
                          narratives...
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Candidate Review */}
            {step === "review" && (
              <div className="space-y-4">
                {/* Control bar */}
                <div className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-800/50">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      Selected:{" "}
                      <strong className="text-[#2ba5b5]">
                        {candidates.filter((c) => c.selected).length}
                      </strong>{" "}
                      of {candidates.length} answers
                    </span>
                    {outcome === "won" && (
                      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                        <Trophy className="h-3 w-3 text-amber-500" /> Won Bid Attribution
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleSelectAll(true)}
                      className="text-[11px] font-medium text-[#2ba5b5] hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      type="button"
                      onClick={() => toggleSelectAll(false)}
                      className="text-[11px] font-medium text-slate-500 hover:underline"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>

                {/* Candidate Cards List */}
                <div className="space-y-3">
                  {candidates.map((c) => {
                    const isExpanded = expandedCandidateId === c.id;
                    const residuals = detectResidualWarnings(c.sanitizedBody);
                    const hasClientMention = Boolean(
                      clientName.trim() &&
                      new RegExp(
                        `\\b${clientName.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
                        "i",
                      ).test(c.sanitizedBody),
                    );

                    return (
                      <div
                        key={c.id}
                        className={`rounded-xl border transition-all ${
                          c.selected
                            ? "border-[#2ba5b5]/50 bg-white dark:bg-slate-900 shadow-2xs"
                            : "border-slate-200 bg-slate-50/70 opacity-60 dark:border-slate-800 dark:bg-slate-800/30"
                        }`}
                      >
                        {/* Header bar of candidate */}
                        <div
                          onClick={() =>
                            setExpandedCandidateId((prev) => (prev === c.id ? null : c.id))
                          }
                          className="flex items-center justify-between p-3.5 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/40"
                        >
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <input
                              type="checkbox"
                              checked={c.selected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleCandidateSelection(c.id);
                              }}
                              className="h-4 w-4 rounded-sm border-slate-300 text-[#2ba5b5] focus:ring-[#2ba5b5]"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-xs text-slate-900 dark:text-white truncate">
                                  {c.title}
                                </span>
                                {c.similarityScore && c.similarityScore >= 0.8 && (
                                  <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                                    {Math.round(c.similarityScore * 100)}% Match to Existing: &quot;
                                    {c.similarToTitle}&quot;
                                  </span>
                                )}
                                {(residuals.length > 0 || hasClientMention) && (
                                  <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800 dark:bg-rose-950 dark:text-rose-300">
                                    {hasClientMention
                                      ? "Contains Client Mention"
                                      : "Residual Warning"}
                                  </span>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 truncate mt-0.5">
                                {c.rationale}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 ml-3">
                            <div className="hidden sm:flex items-center gap-1">
                              {c.serviceTags.slice(0, 2).map((t) => (
                                <span
                                  key={t}
                                  className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                >
                                  {t}
                                </span>
                              ))}
                              {c.serviceTags.length > 2 && (
                                <span className="text-[10px] text-slate-400">
                                  +{c.serviceTags.length - 2}
                                </span>
                              )}
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                        </div>

                        {/* Expanded details editor */}
                        {isExpanded && (
                          <div className="border-t border-slate-100 p-4 space-y-3 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/50">
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1">
                                Answer Title
                              </label>
                              <input
                                type="text"
                                value={c.title}
                                onChange={(e) => updateCandidateTitle(c.id, e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                              />
                            </div>

                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400">
                                  Sanitized Narrative Body (Procurement-Ready)
                                </label>
                                {(residuals.length > 0 || hasClientMention) && (
                                  <span className="text-[10px] text-rose-600 dark:text-rose-400 font-medium">
                                    {hasClientMention
                                      ? `Warning: Check for client reference (${clientName})`
                                      : `Warning: ${residuals[0]}`}
                                  </span>
                                )}
                              </div>
                              <textarea
                                rows={5}
                                value={c.sanitizedBody}
                                onChange={(e) => updateCandidateBody(c.id, e.target.value)}
                                className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-800 leading-relaxed focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                              />
                            </div>

                            {/* Service Tags Picker */}
                            <div>
                              <label className="block text-[11px] font-semibold text-slate-600 dark:text-slate-400 mb-1.5">
                                Service Tags
                              </label>
                              <div className="flex flex-wrap gap-1.5">
                                {DEFAULT_SERVICE_TAGS.map((tag) => {
                                  const isTagged = c.serviceTags.includes(tag);
                                  return (
                                    <button
                                      key={tag}
                                      type="button"
                                      onClick={() => toggleCandidateTag(c.id, tag)}
                                      className={`rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                        isTagged
                                          ? "bg-[#2ba5b5] text-white"
                                          : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"
                                      }`}
                                    >
                                      {tag}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 3: Success Screen */}
            {step === "success" && (
              <div className="py-8 text-center space-y-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-400">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-lg font-bold text-slate-900 dark:text-white">
                    {savedCount} Answers Successfully Added to Library!
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                    Narratives from &ldquo;{proposalTitle}&rdquo; are now available for AI drafting,
                    search, and automatic requirement matching in future bids.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer Bar */}
          <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            {step === "upload" && (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="btn-animate rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAnalyze}
                  disabled={isProcessing || !selectedFile || !proposalTitle.trim()}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white hover:bg-[#238a97] disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <span>Analyze & Extract Answers</span>
                    </>
                  )}
                </button>
              </>
            )}

            {step === "review" && (
              <>
                <button
                  type="button"
                  onClick={() => setStep("upload")}
                  disabled={isProcessing}
                  className="btn-animate rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                >
                  Back to Upload
                </button>
                <button
                  type="button"
                  onClick={handleSaveSelected}
                  disabled={isProcessing || candidates.filter((c) => c.selected).length === 0}
                  className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white hover:bg-[#238a97] disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Saving Answers...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      <span>
                        Save {candidates.filter((c) => c.selected).length} Answers to Library
                      </span>
                    </>
                  )}
                </button>
              </>
            )}

            {step === "success" && (
              <div className="w-full flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-animate rounded-lg bg-[#2ba5b5] px-5 py-2 text-xs font-semibold text-white hover:bg-[#238a97]"
                >
                  Close & View in Library
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export const ImportProposalModal: React.FC<ImportProposalModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <ImportProposalContent key="import-proposal-dialog" {...props} />;
};
